import { useState, useRef, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useChartThemeColors } from './theme-colors-context'
import {
  Toggle,
  DrawerSelect,
  SettingRow,
  PickerSwatch,
  FollowThemeReset,
  ThemedColorRow,
  NumberRow,
  DrawerNumberInput,
} from './drawerControls'
import {
  DEFAULT_SETTINGS,
  defaultAxisTextColor,
  styleGroupOf,
  chartTypeForGroup,
  effectiveGridMode,
  isHollowCandles,
  hollowToggled,
  isHollowHeikinAshi,
  heikinHollowToggled,
  isAreaFilled,
  areaFillToggled,
  type ChartSettings,
  type ChartSettingsProfile,
  type StyleGroup,
  type TouchInput,
} from './chartSettings'
import { BetaTag } from './BetaTag'
import { primaryButtonCls } from './drawerControls-utils'
import { usePanelPosition } from '../hooks/usePanelPosition'
import { useDraggablePanel } from '../hooks/useDraggablePanel'
import { usePortalDismiss } from '../hooks/usePortalDismiss'
import {
  effectiveColor,
  withBodyDerived,
  isThemedColorSetting,
  customizedColorsAfterPick,
  clearColorCustomized,
  type ThemedColorKey,
  type ThemedColors,
} from '../utils/themedColors'
import {
  DEFAULT_TPO_SETTINGS,
  normalizeTpoSettings,
  type TpoLevelStyle,
  type TpoLineStyle,
  type TpoSettings,
} from '../utils/tpoSettings'
import {
  DEFAULT_INTERACTION_SETTINGS,
  INTERACTION_PRESETS,
  type ChartInteractionSettings,
} from '../types/interaction'
import {
  RESET_ZOOM_CAPS,
  clampResetZoomRule,
  type ResetZoomRule,
  type ZoomCategory,
} from '../types/resetZoom'
import { Z_MODAL } from './zLayers'

// Settings state + the chart-type family helpers moved to chartSettings.ts
// (react-refresh: .tsx files export components only). The TYPES are re-exported
// here so existing `import type { ChartSettings } from './SettingsModal'` sites
// keep working; VALUE imports (DEFAULT_SETTINGS, styleGroupOf, ...) resolve in
// chartSettings.ts / the package barrel.
export type { ChartSettings, ChartSettingsProfile, StyleGroup } from './chartSettings'

/** Case/whitespace-insensitive color-string equality (hex compare). */
function sameColor(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/* ------------------------------------------------------------------ */
/*  Inline icons (charts-core has no icon dependency)                  */
/* ------------------------------------------------------------------ */

function Icon({ children, size = 14 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

function EyeIcon() {
  return (
    <Icon>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  )
}

function EyeOffIcon() {
  return (
    <Icon>
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </Icon>
  )
}

function TrashIcon() {
  return (
    <Icon size={12}>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </Icon>
  )
}

function BookmarkIcon() {
  return (
    <Icon size={12}>
      <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
    </Icon>
  )
}

/** Marks a section as per-user global (design D5) — scope is communicated by
 *  section, not per-row badges, so this appears once in the section header. */
/** Corner arrow (down, then right) marking a sub-row that belongs to the row above it. */
function CornerDownRightIcon() {
  return (
    <Icon size={12}>
      <polyline points="15 10 20 15 15 20" />
      <path d="M4 4v7a4 4 0 0 0 4 4h12" />
    </Icon>
  )
}

function GlobeIcon() {
  return (
    <Icon size={12}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </Icon>
  )
}

/* ------------------------------------------------------------------ */
/*  Section + row grammar                                              */
/* ------------------------------------------------------------------ */

/**
 * Section = heading row (label + collapse chevron) + rounded card of rows.
 * Collapsible via local state, default open.
 *
 * `icon` + `subtitle` are optional and only used by sections that need to
 * call out a scope different from "this chart" (design D5 — a setting's
 * location tells you its scope; the Interaction section is the one place
 * that isn't per-chart).
 */
function Section({
  title,
  icon,
  subtitle,
  children,
}: {
  title: string
  icon?: ReactNode
  subtitle?: string
  children: ReactNode
}) {
  // Not collapsible: with the left nav each pane is already short, and an
  // accordion just adds a click (and a place for rows to hide).
  //
  // Flat sections (no tinted card — controls kept losing contrast against it),
  // separated by whitespace + a hairline above each section after the first —
  // the same border token the drawers' figure rows separate with.
  return (
    <div className="mt-5 border-t border-border-strong/60 pt-3.5 first:mt-0 first:border-t-0 first:pt-0">
      <div className="px-1 pb-1.5">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
          {icon}
          {title}
        </div>
        {subtitle && (
          <div className="mt-0.5 text-[10px] font-normal normal-case tracking-normal text-[hsl(var(--chart-text-muted))]">
            {subtitle}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 px-1">{children}</div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Chart-type cards ("Style" selector)                                */
/* ------------------------------------------------------------------ */

const TYPE_CARD_ICONS: Record<string, ReactNode> = {
  // Market Profile: stacked rows of blocks tapering to a point of control.
  tpo: (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor">
      <rect x="4" y="5" width="5" height="3" rx="0.5" />
      <rect x="4" y="9" width="9" height="3" rx="0.5" />
      <rect x="4" y="13" width="14" height="3" rx="0.5" />
      <rect x="4" y="17" width="7" height="3" rx="0.5" />
    </svg>
  ),
  candle_solid: (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor">
      <path d="M9 4v4M9 14v6M15 4v6M15 18v2" fill="none" strokeWidth={1.6} />
      <rect x="7" y="8" width="4" height="6" rx="0.5" />
      <rect x="13" y="10" width="4" height="8" rx="0.5" />
    </svg>
  ),
  // Heikin Ashi: three stepped same-direction bodies, the smoothed-trend read.
  heikin_ashi: (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor">
      <path d="M5 8V5M5 15v3M11 6V3M11 17v2M17 5V3M17 19v2" fill="none" strokeWidth={1.6} />
      <rect x="3" y="8" width="4" height="7" rx="0.5" />
      <rect x="9" y="6" width="4" height="11" rx="0.5" />
      <rect x="15" y="5" width="4" height="14" rx="0.5" />
    </svg>
  ),
  ohlc: (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
    >
      <path d="M8 4v14M8 8H5M8 13h3M16 7v14M16 10h-3M16 17h3" />
    </svg>
  ),
  line: (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 20L8 14l4 4 9-11" />
    </svg>
  ),
}

/** Dash options offered for every TPO level. */
const TPO_LINE_STYLES: Array<{ value: TpoLineStyle; label: string }> = [
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
]

/**
 * One TPO level's four controls on a single row — colour, dash, reset, eye.
 *
 * The reference panel puts these together, and no existing row in this codebase
 * combines all four (FigureStyleRow in IndicatorSettingsDrawer is the closest but
 * uses a checkbox and has no per-row reset), so this composes the file's own
 * primitives rather than adding a fourth colour-row variant.
 */
function TpoLevelRow({
  label,
  value,
  defaultValue,
  onChange,
}: {
  label: string
  value: TpoLevelStyle
  defaultValue: TpoLevelStyle
  onChange: (next: TpoLevelStyle) => void
}) {
  const isDefault =
    value.color === defaultValue.color &&
    value.lineStyle === defaultValue.lineStyle &&
    value.visible === defaultValue.visible
  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        <PickerSwatch
          title={label}
          value={value.color}
          onChange={(color) => onChange({ ...value, color })}
        />
        <DrawerSelect
          ariaLabel={`${label} line style`}
          value={value.lineStyle}
          options={TPO_LINE_STYLES}
          onChange={(v) => onChange({ ...value, lineStyle: v as TpoLineStyle })}
        />
        {!isDefault && <FollowThemeReset onReset={() => onChange(defaultValue)} label={label} />}
        <button
          type="button"
          aria-label={`${label} visibility`}
          aria-pressed={value.visible}
          onClick={() => onChange({ ...value, visible: !value.visible })}
          className="rounded p-1 text-muted-foreground hover:text-foreground"
        >
          {value.visible ? <EyeIcon /> : <EyeOffIcon />}
        </button>
      </div>
    </div>
  )
}

const STYLE_GROUP_CARDS: Array<{
  group: StyleGroup
  label: string
  icon: ReactNode
  beta?: true
}> = [
  { group: 'candles', label: 'Candles', icon: TYPE_CARD_ICONS.candle_solid },
  { group: 'line', label: 'Line', icon: TYPE_CARD_ICONS.line },
  { group: 'bars', label: 'Bars', icon: TYPE_CARD_ICONS.ohlc },
  { group: 'tpo', label: 'TPO', icon: TYPE_CARD_ICONS.tpo },
  { group: 'heikin', label: 'Heikin', icon: TYPE_CARD_ICONS.heikin_ashi },
]

// Tailwind needs the class spelled out, so the column count maps here rather
// than being interpolated. One column per card keeps every family on one row —
// at grid-cols-3 the fourth card wrapped onto its own line.
const GRID_COLS: Record<number, string> = {
  4: 'grid-cols-4',
  5: 'grid-cols-5',
}

/** Tab-like style-family cards (reference-app style): the selected card gets
 *  the primary ring and reveals that family's color rows beneath. */
function StyleGroupCards({
  group,
  cards,
  onChange,
}: {
  group: StyleGroup
  cards: typeof STYLE_GROUP_CARDS
  onChange: (group: StyleGroup) => void
}) {
  return (
    <div
      className={'grid gap-1.5 ' + (GRID_COLS[cards.length] ?? 'grid-cols-4')}
      data-testid="style-group-cards"
    >
      {cards.map((card) => {
        const active = card.group === group
        return (
          <button
            key={card.group}
            type="button"
            title={card.label}
            onClick={() => onChange(card.group)}
            className={
              'flex flex-col items-center gap-1.5 rounded-lg border px-1 py-2.5 transition-colors ' +
              (active
                ? 'border-primary bg-primary/10 text-[hsl(var(--chart-text))]'
                : 'border-[hsl(var(--chart-text)/0.12)] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--chart-text)/0.3)] hover:text-[hsl(var(--chart-text))]')
            }
          >
            {card.icon}
            <span className="flex items-center gap-1 text-[11px] leading-none">
              {card.label}
              {card.beta ? <BetaTag /> : null}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/** Up/down color pair merged into one row — two swatches side by side. */
/** Small icon button used by the compact Grid/Crosshair rows. */
function IconButton({
  active,
  title,
  ariaLabel,
  onClick,
  pressed,
  children,
}: {
  active?: boolean
  title: string
  ariaLabel: string
  onClick: () => void
  pressed?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={ariaLabel}
      aria-pressed={pressed}
      onClick={onClick}
      className={
        'flex h-5 w-6 items-center justify-center rounded-sm transition-colors ' +
        (active
          ? 'bg-accent text-[hsl(var(--chart-text))]'
          : 'text-[hsl(var(--muted-foreground))] hover:bg-accent/60 hover:text-[hsl(var(--chart-text))]')
      }
    >
      {children}
    </button>
  )
}

function EyeToggle({
  shown: isShown,
  label,
  onChange,
}: {
  shown: boolean
  label: string
  onChange: (next: boolean) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!isShown)}
      title={isShown ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
      aria-label={`${isShown ? 'Hide' : 'Show'} ${label}`}
      aria-pressed={!isShown}
      className={
        'flex h-5 w-5 items-center justify-center rounded-sm transition-colors hover:bg-accent ' +
        (isShown
          ? 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--chart-text))]'
          : 'text-[hsl(var(--chart-text-muted))] opacity-60')
      }
    >
      {isShown ? <EyeIcon /> : <EyeOffIcon />}
    </button>
  )
}

const CROSSHAIR_LINE_OPTIONS = [
  { value: 'both', title: 'Both', icon: 'M12 4v16M4 12h16' },
  { value: 'horizontal', title: 'Horizontal (price) line', icon: 'M4 12h16' },
  { value: 'vertical', title: 'Vertical (time) line', icon: 'M12 4v16' },
] as const

const GRID_LINE_OPTIONS = [
  { value: 'both', title: 'Both', icon: 'M4 9h16M4 15h16M9 4v16M15 4v16' },
  { value: 'horizontal', title: 'Horizontal lines', icon: 'M4 7h16M4 12h16M4 17h16' },
  { value: 'vertical', title: 'Vertical lines', icon: 'M7 4v16M12 4v16M17 4v16' },
] as const

function ColorPairRow({
  label,
  upValue,
  downValue,
  onUpChange,
  onDownChange,
  onReset,
  eyeShown,
  onEyeChange,
}: {
  label: string
  upValue: string
  downValue: string
  onUpChange: (v: string) => void
  onDownChange: (v: string) => void
  /** Present ⇒ at least one of the pair overrides the theme. Resets both, so
   *  the up/down pair can never end up half-themed (which would read as a bug). */
  onReset?: () => void
  /** Present (with onEyeChange) ⇒ the row tail carries an eye toggling the
   *  element's visibility — the swatches + conditional-reset + eye idiom
   *  shared with the indicator drawer's paired style rows. */
  eyeShown?: boolean
  onEyeChange?: (shown: boolean) => void
}) {
  return (
    <SettingRow label={label}>
      <div className="flex items-center gap-1.5">
        {onReset && <FollowThemeReset onReset={onReset} label={label} />}
        <PickerSwatch value={upValue} onChange={onUpChange} title={`${label} (up)`} />
        <PickerSwatch value={downValue} onChange={onDownChange} title={`${label} (down)`} />
        {eyeShown !== undefined && onEyeChange && (
          <EyeToggle shown={eyeShown} label={label} onChange={onEyeChange} />
        )}
      </div>
    </SettingRow>
  )
}

function ToggleRow({
  label,
  badge,
  value,
  onChange,
}: {
  label: string
  /** Optional chip after the label (a Beta tag); the aria-label stays `label`. */
  badge?: ReactNode
  value: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <SettingRow label={label} badge={badge}>
      <Toggle checked={value} onChange={onChange} size="sm" ariaLabel={label} />
    </SettingRow>
  )
}

export { Section as SettingsSection, ToggleRow as SettingsToggleRow }

/**
 * Visibility row — eye icon-toggle. `hidden` mirrors the underlying `hideX`
 * setting key; the eye shows OPEN when the element is visible (hidden=false).
 */
function EyeRow({
  label,
  hidden,
  onChange,
}: {
  label: string
  hidden: boolean
  onChange: (hidden: boolean) => void
}) {
  return (
    <SettingRow label={label}>
      <button
        type="button"
        aria-pressed={!hidden}
        title={hidden ? `Show ${label.toLowerCase()}` : `Hide ${label.toLowerCase()}`}
        onClick={() => onChange(!hidden)}
        className={
          'flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-accent ' +
          (hidden ? 'text-[hsl(var(--muted-foreground))]' : 'text-[hsl(var(--chart-text))]')
        }
      >
        {hidden ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </SettingRow>
  )
}

/** Settings-modal select trigger. Auto width up to a cap: option labels like
 *  "Zoom to selection" and "Prior after-hours" are far wider than a w-24 box. */
const SELECT_BOX_CLS =
  'h-7 min-w-32 max-w-[200px] rounded border border-border-strong bg-field px-2 text-xs text-[hsl(var(--chart-text))] outline-hidden focus:border-primary'

function SelectRow({
  label,
  ariaLabel,
  value,
  options,
  onChange,
}: {
  label: string
  /** Distinct accessible name when the visible label repeats across sections. */
  ariaLabel?: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <SettingRow label={label}>
      <DrawerSelect
        ariaLabel={ariaLabel ?? label}
        value={value}
        options={options}
        onChange={onChange}
        buttonClassName={SELECT_BOX_CLS}
      />
    </SettingRow>
  )
}

/** Flat-key equality for `ChartInteractionSettings` — three string enum
 *  fields, so a manual compare beats pulling in a deep-equal dependency. */
function interactionEquals(a: ChartInteractionSettings, b: ChartInteractionSettings): boolean {
  return (
    a.wheelAction === b.wheelAction &&
    a.dragAction === b.dragAction &&
    a.zoomAnchor === b.zoomAnchor
  )
}

/**
 * Built-in interaction preset picker — a dropdown over `INTERACTION_PRESETS`.
 * The selected entry is the preset the current settings deep-equal exactly;
 * a manual tweak away from any preset shows as "Custom" (a display-only
 * state, never a selectable action). Picking a preset loads its FULL
 * settings object, replacing all three keys at once (see design D2/D5).
 */
function InteractionPresetRow({
  interactionSettings,
  onChange,
}: {
  interactionSettings: ChartInteractionSettings
  onChange: (patch: Partial<ChartInteractionSettings>) => void
}) {
  const activeId =
    INTERACTION_PRESETS.find((p) => interactionEquals(interactionSettings, p.settings))?.id ??
    'custom'
  return (
    <SelectRow
      label="Preset"
      value={activeId}
      options={[
        ...INTERACTION_PRESETS.map((p) => ({ value: p.id, label: p.name })),
        // Only present while active, so it displays but can't be "chosen".
        ...(activeId === 'custom' ? [{ value: 'custom', label: 'Custom' }] : []),
      ]}
      onChange={(v) => {
        const preset = INTERACTION_PRESETS.find((p) => p.id === v)
        if (preset) onChange({ ...preset.settings })
      }}
    />
  )
}

const ZOOM_LEVEL_OPTIONS = [
  { value: 'in', label: 'Zoomed in' },
  { value: 'standard', label: 'Standard' },
  { value: 'out', label: 'Zoomed out' },
] as const

/** The range option(s) each row offers after the three levels (spec D9). */
const RESET_ZOOM_RANGE_OPTIONS: Record<ZoomCategory, { value: string; label: string }[]> = {
  intraday: [
    { value: 'anchor:open', label: 'Session open' },
    { value: 'anchor:premarket', label: 'Pre-market' },
    { value: 'anchor:yesterdayAfterHours', label: 'Prior after-hours' },
  ],
  hourly: [{ value: 'daysBack', label: 'Days back' }],
  daily: [{ value: 'daysBack', label: 'Days back' }],
  weeklyMonthly: [{ value: 'barsBack', label: 'Bars back' }],
}

const RESET_ZOOM_NUMBER_LABEL: Record<ZoomCategory, string> = {
  intraday: 'Minimum bars',
  hourly: 'Days',
  daily: 'Days',
  weeklyMonthly: 'Bars',
}

/** Select value for a stored rule; an absent rule reads as Standard. */
function resetZoomSelectValue(rule: ResetZoomRule | undefined): string {
  if (!rule) return 'standard'
  switch (rule.kind) {
    case 'level':
      return rule.level
    case 'anchor':
      return `anchor:${rule.anchor}`
    default:
      return rule.kind
  }
}

/** The number carried by a range rule (undefined for a level). */
function resetZoomRuleCount(rule: ResetZoomRule | undefined): number | undefined {
  if (!rule) return undefined
  switch (rule.kind) {
    case 'anchor':
      return rule.minBars
    case 'daysBack':
      return rule.days
    case 'barsBack':
      return rule.bars
    default:
      return undefined
  }
}

/**
 * One "Reset zoom" row: a select over the three zoom levels plus the row's
 * range option(s), and, while a range is chosen, a number row beneath it.
 * Every number edit runs through `clampResetZoomRule` so the D7 cap holds
 * even though `NumberRow` has no `max` (spec D9).
 */
function ResetZoomRow({
  category,
  label,
  rule,
  onChange,
}: {
  category: ZoomCategory
  label: string
  rule: ResetZoomRule | undefined
  onChange: (rule: ResetZoomRule) => void
}) {
  const cap = RESET_ZOOM_CAPS[category]
  const count = resetZoomRuleCount(rule)

  const emitCount = (n: number) => {
    // Rebuild from the current rule so an anchor pick keeps its anchor.
    const candidate =
      rule?.kind === 'anchor'
        ? { kind: 'anchor', anchor: rule.anchor, minBars: n }
        : { kind: cap.kind, [cap.field]: n }
    const clean = clampResetZoomRule(category, candidate)
    if (clean) onChange(clean)
  }

  // Compact rows (Chris, 2026-09-22): no preset chips. "Days back" and
  // "Bars back" already name their unit, so their number sits beside the
  // select on the same line. An anchor's number is a floor with its own
  // meaning, so it gets a second, indented "Minimum bars" line under the row.
  const numberBox = count !== undefined && (
    <DrawerNumberInput
      ariaLabel={RESET_ZOOM_NUMBER_LABEL[category]}
      value={count}
      min={1}
      max={cap.max}
      step={1}
      integer
      onChange={emitCount}
      className="h-7 w-14 rounded border border-border-strong bg-field px-1.5 text-right text-xs text-[hsl(var(--chart-text))] outline-hidden focus:border-primary"
    />
  )
  const isAnchor = rule?.kind === 'anchor'

  return (
    <>
      <SettingRow label={label}>
        <div className="flex items-center gap-1.5">
          <DrawerSelect
            ariaLabel={label}
            value={resetZoomSelectValue(rule)}
            options={[...ZOOM_LEVEL_OPTIONS, ...RESET_ZOOM_RANGE_OPTIONS[category]]}
            onChange={(v) => {
              if (v === 'in' || v === 'standard' || v === 'out') {
                onChange({ kind: 'level', level: v })
                return
              }
              const candidate = v.startsWith('anchor:')
                ? { kind: 'anchor', anchor: v.slice('anchor:'.length), minBars: cap.default }
                : { kind: v, [cap.field]: cap.default }
              const clean = clampResetZoomRule(category, candidate)
              if (clean) onChange(clean)
            }}
            buttonClassName={SELECT_BOX_CLS}
          />
          {!isAnchor && numberBox}
        </div>
      </SettingRow>
      {isAnchor && (
        // Same grammar as SettingRow, with the label indented under its parent row.
        <div className="flex min-h-6 items-center justify-between pl-3">
          <span className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
            <CornerDownRightIcon />
            {RESET_ZOOM_NUMBER_LABEL[category]}
          </span>
          {numberBox}
        </div>
      )}
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  Tab bar                                                            */
/* ------------------------------------------------------------------ */

const NAV_PANES = [
  'Chart style',
  'Canvas',
  'Scales & zoom',
  'Session & data',
  'Markers & labels',
] as const
type NavPane = (typeof NAV_PANES)[number]

/* ------------------------------------------------------------------ */
/*  Modal                                                              */
/* ------------------------------------------------------------------ */

export interface SettingsModalProps {
  settings: ChartSettings
  onChange: (settings: ChartSettings) => void
  onClose: () => void
  /**
   * Saved snapshots of `ChartSettings`. The application owns the list
   * and persistence; the modal just renders + emits callbacks.
   */
  settingsProfiles?: ChartSettingsProfile[]
  /** User clicked save with a name. App snapshots current `settings` under that name. */
  onSaveSettingsProfile?: (name: string) => void
  /** User picked a profile to load. App replaces panel settings with the profile's payload. */
  onLoadSettingsProfile?: (profileId: string) => void
  /** User asked to delete a profile. */
  onDeleteSettingsProfile?: (profileId: string) => void
  /**
   * Optional host-supplied settings rows rendered below the built-in
   * sections. Lets a consumer (e.g. the backtest grid) add its own toggles
   * without widening the shared `ChartSettings` type.
   */
  extraSections?: ReactNode
  /**
   * Chart type (candle_solid / candle_stroke / ohlc / area / line). When both
   * this and `onChartTypeChange` are provided, the General tab leads with a
   * card-style type selector ("Style" cards). Chart type
   * is toolbar state, not part of ChartSettings — hence the separate wiring.
   */
  chartType?: string
  onChartTypeChange?: (type: string) => void
  /**
   * Per-user global interaction settings (wheel/drag grammar + zoom anchor),
   * rendered in a dedicated "Interaction" section (design D5). Deliberately
   * separate from `settings`/`onChange` — those two make the write path
   * structurally incapable of landing in per-panel `ChartSettings`. Defaults
   * to `DEFAULT_INTERACTION_SETTINGS` / a no-op when the host hasn't wired
   * global interaction storage.
   */
  interactionSettings?: ChartInteractionSettings
  onInteractionSettingsChange?: (patch: Partial<ChartInteractionSettings>) => void
  /**
   * Viewport coordinates of the click that opened the modal. Legacy placement —
   * `centerIn` wins when both are provided.
   */
  anchor?: { x: number; y: number }
  /** Element to center the panel over (the chart area). Preferred placement:
   *  a settings dialog belongs over the surface it configures, clamped to the
   *  viewport — same call the indicator drawer makes. */
  centerIn?: { current: HTMLElement | null }
  /**
   * The active app theme's color for each themed setting (from
   * `themedColorsFrom(applyTheme(theme))`). Any key the user hasn't explicitly
   * picked follows these — so the swatch must render THIS value, not the
   * stored fallback, or the panel shows a color that isn't on the chart.
   *
   * Omit it and every swatch falls back to the stored value (the pre-theme
   * behavior) — correct, just less informative.
   */
  themeColors?: ThemedColors
  /**
   * Price alerts are released for this user and this host can draw alert lines (chart
   * crosshair alert spec, C2 and C4): "Markers & labels" shows the "Alert lines" switch.
   * Off, the row is hidden.
   */
  priceAlertsAvailable?: boolean
}

export function SettingsModal({
  settings,
  onChange,
  onClose,
  settingsProfiles,
  onSaveSettingsProfile,
  onLoadSettingsProfile,
  onDeleteSettingsProfile,
  extraSections,
  chartType,
  onChartTypeChange,
  interactionSettings = DEFAULT_INTERACTION_SETTINGS,
  onInteractionSettingsChange = () => {},
  anchor,
  centerIn,
  themeColors,
  priceAlertsAvailable = false,
}: SettingsModalProps) {
  const [pane, setPane] = useState<NavPane>('Chart style')
  const [profileName, setProfileName] = useState('')
  const [templatesOpen, setTemplatesOpen] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  // Style-family card selection: seeded from (and kept in sync with) the live
  // chart type; clicking a card also switches the chart into that family via
  // its representative type — unless the chart is already in the family (so
  // "Candles" never demotes hollow candles to solid).
  const [styleGroup, setStyleGroup] = useState<StyleGroup>(() => styleGroupOf(chartType))
  useEffect(() => {
    setStyleGroup(styleGroupOf(chartType))
  }, [chartType])
  const handleStyleGroupChange = useCallback(
    (g: StyleGroup) => {
      setStyleGroup(g)
      if (onChartTypeChange && styleGroupOf(chartType) !== g) {
        onChartTypeChange(chartTypeForGroup(g))
      }
    },
    [chartType, onChartTypeChange],
  )

  // Anchored placement: measure the panel, place it beside the opening click
  // (clamped). Two-pass — hidden until measured. `null` when un-anchored (the
  // modal keeps its default right-edge placement).
  const anchorPos = usePanelPosition(modalRef, {
    // Centering over the chart wins; the click-anchor is the legacy fallback.
    anchor: centerIn ? undefined : anchor,
    centerIn,
    fallback: centerIn ? undefined : 'none',
  })

  // Header-drag position. Once the user drags, this overrides the measured
  // placement for the rest of the session; reopening recenters (the modal
  // unmounts on close, so the state simply resets). Also handles the
  // window-resize clamp so a dragged panel can't slide off-screen.
  const { pos: dragPos, onHeaderPointerDown: handleHeaderPointerDown } = useDraggablePanel(
    modalRef,
    anchorPos,
  )

  // Esc + outside-click close. The gear's own onClick toggle is guarded so a
  // second click doesn't bounce-reopen; the host color picker's Radix portal is
  // guarded inside the hook.
  usePortalDismiss(modalRef, onClose, { guardSelector: '[data-action="settings"]' })

  // Host-injected theme colors, used when the caller didn't pass any. Panel-
  // level modals (dashboard / scanner / history grids) rely on this rather than
  // each threading the prop down.
  const hostThemeColors = useChartThemeColors()

  /**
   * What a themed color swatch must DISPLAY: the same value `useChartStyles`
   * paints with. Showing `settings[key]` instead makes the panel lie whenever
   * the key is following the theme — the swatch reads the stored fallback
   * (klinecharts' factory teal) while the canvas draws the theme's color.
   *
   * Defined above `set` because `set` compares picks against these theme values.
   */
  // Normalized here rather than trusting the caller: `withBodyDerived` is
  // idempotent, so a host that passes raw theme values still gets correct
  // border/wick swatches instead of silently falling back to the stored ones.
  const colorDefaults = useMemo(
    // Merge, don't replace. The `themeColors` prop (EdgeChart's own theme bundle)
    // carries candle/grid/crosshair/separator but NOT the session-overlay defaults
    // (pre-market / after-hours / day break) — those are read from the klinecharts
    // style bundle by `themedColorsFrom`, which has no session slots, and live only
    // on the host context (ChartThemeColorsContext). Spreading the prop OVER the
    // context keeps the prop authoritative per-key while letting the session
    // defaults through, so their swatches show the ACTIVE theme color instead of
    // the stored fallback (which read as a gray — the old rgba(82,82,82,…)).
    () => withBodyDerived({ ...(hostThemeColors ?? {}), ...(themeColors ?? {}) }, settings),
    [themeColors, hostThemeColors, settings],
  )

  // Every change goes through a single `onChange(ChartSettings)` callback —
  // the modal no longer partitions keys by tab. The consumer can route
  // wherever it likes (panel state, profile, etc).
  const set = useCallback(
    <K extends keyof ChartSettings>(key: K, val: ChartSettings[K]) => {
      // Picking a themed color records intent to detach from the app theme, with
      // one exception: choosing the exact color the theme already paints re-
      // attaches the key (clears the flag) rather than pinning an identical
      // color. Without this, clicking the swatch that equals the theme color
      // raised a reset arrow even though nothing visibly changed.
      const customizedColors =
        isThemedColorSetting(key) && typeof val === 'string'
          ? customizedColorsAfterPick(
              settings.customizedColors,
              key,
              val,
              colorDefaults[key as ThemedColorKey],
            )
          : settings.customizedColors
      onChange({ ...settings, [key]: val, customizedColors })
    },
    [settings, onChange, colorDefaults],
  )

  /**
   * Complete TPO settings, with any legacy/partial persisted payload topped up.
   *
   * Read from `tpoSettings` rather than `settings.tpo` everywhere below: layout
   * persistence merges shallowly, so a `tpo` saved before a field existed arrives
   * missing it, and reading a nested field off that threw.
   */
  const tpoSettings = useMemo(() => normalizeTpoSettings(settings.tpo), [settings.tpo])

  /** Patch a subset of the nested TPO settings, leaving the rest untouched. */
  const setTpo = useCallback(
    (patch: Partial<TpoSettings>) => {
      // Patched onto the NORMALIZED object, so editing one row cannot persist a
      // still-incomplete `tpo` back to the layout.
      onChange({ ...settings, tpo: { ...tpoSettings, ...patch } })
    },
    [settings, tpoSettings, onChange],
  )

  const shown = useCallback(
    (key: ThemedColorKey): string =>
      effectiveColor(key, settings[key], colorDefaults, settings.customizedColors),
    [settings, colorDefaults],
  )

  /** True when this key currently overrides the theme (drives the reset arrow). */
  const overrides = useCallback(
    (...keys: ThemedColorKey[]): boolean =>
      keys.some((k) => settings.customizedColors?.includes(k)),
    [settings.customizedColors],
  )

  /** Hand the given keys back to the theme. */
  const resetToTheme = useCallback(
    (...keys: ThemedColorKey[]) => {
      let next = settings.customizedColors
      for (const k of keys) next = clearColorCustomized(next, k)
      onChange({ ...settings, customizedColors: next })
    },
    [settings, onChange],
  )

  const profilesEnabled = !!settingsProfiles && !!onSaveSettingsProfile && !!onLoadSettingsProfile

  const handleSaveProfile = useCallback(() => {
    const name = profileName.trim()
    if (!name || !onSaveSettingsProfile) return
    onSaveSettingsProfile(name)
    setProfileName('')
  }, [profileName, onSaveSettingsProfile])

  const panel = (
    <div
      ref={modalRef}
      data-chart-popup=""
      // Fixed-position panel portaled to <body> — not constrained by the
      // chart's height. Background = chart canvas = theme background, via
      // --chart-canvas; design primitives come from app tokens.
      className="fixed flex w-[480px] flex-col overflow-hidden rounded-xl border border-primary/15 bg-popover shadow-2xl shadow-black/50"
      style={{
        zIndex: Z_MODAL,
        // FIXED height, not a max: pane content varies wildly (Chart style vs
        // Crosshair) and a content-driven height made the modal resize on
        // every nav click — and grow past the viewport bottom after opening,
        // since the position is measured once. Fixed, the clamp in
        // usePanelPosition guarantees the bottom stays on-screen.
        //
        // 650 is tuned to the Session & data pane — the tallest pane that
        // should fit WITHOUT scrolling (worst case ≈625px: 10 rows across 4
        // section cards with every conditional row shown, plus header/footer).
        // Chart style and Canvas exceed it by design and scroll internally;
        // shorter panes leave room.
        height: 'min(650px, 90vh)',
        ...(dragPos ??
          anchorPos ??
          // Default: hug the right edge, vertically centered. Also the first
          // paint while an anchored position is being measured (hidden).
          ({
            top: '50%',
            right: 24,
            transform: 'translateY(-50%)',
            visibility: anchor || centerIn ? 'hidden' : undefined,
          } as const)),
      }}
    >
      {/* Header (sticky) — also the drag handle. touch-none keeps a touch
          drag from scrolling the page instead of moving the panel. */}
      <div
        onPointerDown={handleHeaderPointerDown}
        className="flex shrink-0 cursor-grab touch-none select-none items-center justify-between border-b border-border-strong px-4 py-3 active:cursor-grabbing"
      >
        <span className="text-[13px] font-semibold text-[hsl(var(--chart-text))]">
          Chart Settings
        </span>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          aria-label="Close settings"
          className="rounded border border-border-strong px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--muted-foreground))] transition-colors hover:bg-accent hover:text-[hsl(var(--chart-text))]"
        >
          Esc
        </button>
      </div>

      {/* Body: left category nav + the active pane */}
      <div className="flex min-h-0 flex-1">
        <nav
          aria-label="Settings categories"
          className="flex w-[118px] shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-border-strong p-2"
        >
          {NAV_PANES.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPane(p)}
              className={
                'w-full rounded px-2 py-1.5 text-left text-[11px] font-medium transition-colors ' +
                (pane === p
                  ? 'bg-accent text-[hsl(var(--chart-text))]'
                  : 'text-[hsl(var(--muted-foreground))] hover:bg-accent/60 hover:text-[hsl(var(--chart-text))]')
              }
            >
              {p}
            </button>
          ))}
        </nav>
        <div className="flex-1 overflow-y-auto px-3 py-3" style={{ scrollbarWidth: 'thin' }}>
          {pane === 'Chart style' && (
            <>
              <Section title="Style">
                {/* Cards are a settings-VIEW switcher, not a chart-type control:
                  each card reveals the color rows for that style family.
                  Candles covers solid + hollow; Line covers line + area;
                  Bars is OHLC (colored by the same up/down pair). */}
                <div className="mb-3">
                  <StyleGroupCards
                    group={styleGroup}
                    cards={STYLE_GROUP_CARDS}
                    onChange={handleStyleGroupChange}
                  />
                </div>
                {styleGroup === 'candles' && (
                  <>
                    {/* Hollow candles has no toggle: Body eye off with the
                        border showing IS hollow (candle_stroke), implicitly. */}
                    <ColorPairRow
                      label="Body"
                      upValue={shown('candleUpColor')}
                      downValue={shown('candleDownColor')}
                      onUpChange={(v) => set('candleUpColor', v)}
                      onDownChange={(v) => set('candleDownColor', v)}
                      onReset={
                        overrides('candleUpColor', 'candleDownColor')
                          ? () => resetToTheme('candleUpColor', 'candleDownColor')
                          : undefined
                      }
                      eyeShown={onChartTypeChange ? !isHollowCandles(chartType) : undefined}
                      onEyeChange={(shownNow) => onChartTypeChange?.(hollowToggled(!shownNow))}
                    />
                    <ColorPairRow
                      label="Border"
                      upValue={shown('candleBorderUpColor')}
                      downValue={shown('candleBorderDownColor')}
                      onUpChange={(v) => set('candleBorderUpColor', v)}
                      onDownChange={(v) => set('candleBorderDownColor', v)}
                      onReset={
                        overrides('candleBorderUpColor', 'candleBorderDownColor')
                          ? () => resetToTheme('candleBorderUpColor', 'candleBorderDownColor')
                          : undefined
                      }
                      eyeShown={settings.showCandleBorder}
                      onEyeChange={(v) => set('showCandleBorder', v)}
                    />
                    <ColorPairRow
                      label="Wicks"
                      upValue={shown('wickUpColor')}
                      downValue={shown('wickDownColor')}
                      onUpChange={(v) => set('wickUpColor', v)}
                      onDownChange={(v) => set('wickDownColor', v)}
                      onReset={
                        overrides('wickUpColor', 'wickDownColor')
                          ? () => resetToTheme('wickUpColor', 'wickDownColor')
                          : undefined
                      }
                      eyeShown={settings.showCandleWicks}
                      onEyeChange={(v) => set('showCandleWicks', v)}
                    />
                  </>
                )}
                {styleGroup === 'heikin' && (
                  <>
                    {/* Same three rows as Candles, on the same colour keys — the
                        averaged candles borrow the candle palette — with the Body
                        eye writing the Heikin hollow modifier instead. */}
                    <ColorPairRow
                      label="Body"
                      upValue={shown('candleUpColor')}
                      downValue={shown('candleDownColor')}
                      onUpChange={(v) => set('candleUpColor', v)}
                      onDownChange={(v) => set('candleDownColor', v)}
                      onReset={
                        overrides('candleUpColor', 'candleDownColor')
                          ? () => resetToTheme('candleUpColor', 'candleDownColor')
                          : undefined
                      }
                      eyeShown={onChartTypeChange ? !isHollowHeikinAshi(chartType) : undefined}
                      onEyeChange={(shownNow) =>
                        onChartTypeChange?.(heikinHollowToggled(!shownNow))
                      }
                    />
                    <ColorPairRow
                      label="Border"
                      upValue={shown('candleBorderUpColor')}
                      downValue={shown('candleBorderDownColor')}
                      onUpChange={(v) => set('candleBorderUpColor', v)}
                      onDownChange={(v) => set('candleBorderDownColor', v)}
                      onReset={
                        overrides('candleBorderUpColor', 'candleBorderDownColor')
                          ? () => resetToTheme('candleBorderUpColor', 'candleBorderDownColor')
                          : undefined
                      }
                      eyeShown={settings.showCandleBorder}
                      onEyeChange={(v) => set('showCandleBorder', v)}
                    />
                    <ColorPairRow
                      label="Wicks"
                      upValue={shown('wickUpColor')}
                      downValue={shown('wickDownColor')}
                      onUpChange={(v) => set('wickUpColor', v)}
                      onDownChange={(v) => set('wickDownColor', v)}
                      onReset={
                        overrides('wickUpColor', 'wickDownColor')
                          ? () => resetToTheme('wickUpColor', 'wickDownColor')
                          : undefined
                      }
                      eyeShown={settings.showCandleWicks}
                      onEyeChange={(v) => set('showCandleWicks', v)}
                    />
                  </>
                )}
                {styleGroup === 'line' && (
                  <>
                    <ToggleRow
                      label="Area fill"
                      value={isAreaFilled(chartType)}
                      onChange={(v) => onChartTypeChange?.(areaFillToggled(v))}
                    />
                    <ThemedColorRow
                      label="Line / Area color"
                      value={settings.lineColor}
                      onChange={(v) => set('lineColor', v)}
                    />
                  </>
                )}
                {styleGroup === 'tpo' && (
                  <>
                    {/* Profile session (all vs regular hours) has NO row here —
                        it derives from Session & data → Extended hours (see
                        useChartStyles' TPO effect). One source of truth. */}
                    <SelectRow
                      label="Row size"
                      value={tpoSettings.rowSizeMode}
                      options={[
                        { value: 'auto', label: 'Auto (volatility)' },
                        { value: 'manual', label: 'Manual' },
                      ]}
                      onChange={(v) => setTpo({ rowSizeMode: v as TpoSettings['rowSizeMode'] })}
                    />
                    {tpoSettings.rowSizeMode === 'manual' && (
                      <NumberRow
                        label="Row width ($)"
                        value={tpoSettings.manualRowSize}
                        min={0}
                        step={0.01}
                        onChange={(v) => setTpo({ manualRowSize: v })}
                      />
                    )}
                    <SelectRow
                      label="Display"
                      value={tpoSettings.display}
                      options={[
                        { value: 'stacked', label: 'Stacked' },
                        { value: 'expanded', label: 'Expanded' },
                      ]}
                      onChange={(v) => setTpo({ display: v as TpoSettings['display'] })}
                    />
                    <ToggleRow
                      label="Blocks"
                      value={tpoSettings.blocks}
                      onChange={(v) => setTpo({ blocks: v })}
                    />
                    <ToggleRow
                      label="Letters"
                      value={tpoSettings.letters}
                      onChange={(v) => setTpo({ letters: v })}
                    />
                    <SelectRow
                      label="Color by"
                      value={tpoSettings.colorBy}
                      options={[
                        { value: 'value-area', label: 'Value Area' },
                        { value: 'time-gradient', label: 'Time Gradient' },
                        { value: 'single', label: 'Single' },
                      ]}
                      onChange={(v) => setTpo({ colorBy: v as TpoSettings['colorBy'] })}
                    />
                    <div className="flex items-center justify-between gap-2 py-1">
                      <span className="text-xs text-muted-foreground">Blocks colors</span>
                      <div className="flex items-center gap-1.5">
                        {tpoSettings.blockColors.map((c, i) => (
                          <PickerSwatch
                            key={i}
                            title={`Block color ${i + 1}`}
                            value={c}
                            onChange={(next) => {
                              const blockColors = [...tpoSettings.blockColors]
                              blockColors[i] = next
                              setTpo({ blockColors })
                            }}
                          />
                        ))}
                        {tpoSettings.blockColors.join() !==
                          DEFAULT_TPO_SETTINGS.blockColors.join() && (
                          <FollowThemeReset
                            onReset={() =>
                              setTpo({ blockColors: DEFAULT_TPO_SETTINGS.blockColors })
                            }
                            label="Blocks colors"
                          />
                        )}
                      </div>
                    </div>
                    <ThemedColorRow
                      label="Single color"
                      value={tpoSettings.singleColor}
                      onChange={(v) => setTpo({ singleColor: v })}
                      onReset={
                        tpoSettings.singleColor !== DEFAULT_TPO_SETTINGS.singleColor
                          ? () => setTpo({ singleColor: DEFAULT_TPO_SETTINGS.singleColor })
                          : undefined
                      }
                    />
                    <NumberRow
                      label="Outside VA opacity %"
                      value={tpoSettings.outsideVaOpacity}
                      min={0}
                      step={5}
                      onChange={(v) => setTpo({ outsideVaOpacity: v })}
                    />
                    <NumberRow
                      label="Profile opacity %"
                      value={tpoSettings.profileOpacity}
                      min={0}
                      step={5}
                      onChange={(v) => setTpo({ profileOpacity: v })}
                    />
                    <ToggleRow
                      label="Volume profile"
                      value={tpoSettings.volumeProfile.visible}
                      onChange={(v) =>
                        setTpo({
                          volumeProfile: { ...tpoSettings.volumeProfile, visible: v },
                        })
                      }
                    />
                    {tpoSettings.volumeProfile.visible && (
                      <>
                        <NumberRow
                          label="Volume width %"
                          value={tpoSettings.volumeProfile.widthPct}
                          min={0}
                          step={5}
                          onChange={(v) =>
                            setTpo({
                              volumeProfile: { ...tpoSettings.volumeProfile, widthPct: v },
                            })
                          }
                        />
                        <ThemedColorRow
                          label="Volume color"
                          value={tpoSettings.volumeProfile.color}
                          onChange={(v) =>
                            setTpo({
                              volumeProfile: { ...tpoSettings.volumeProfile, color: v },
                            })
                          }
                          onReset={
                            tpoSettings.volumeProfile.color !==
                            DEFAULT_TPO_SETTINGS.volumeProfile.color
                              ? () =>
                                  setTpo({
                                    volumeProfile: {
                                      ...tpoSettings.volumeProfile,
                                      color: DEFAULT_TPO_SETTINGS.volumeProfile.color,
                                    },
                                  })
                              : undefined
                          }
                        />
                        <ThemedColorRow
                          label="Volume POC"
                          value={tpoSettings.volumeProfile.pocColor}
                          onChange={(v) =>
                            setTpo({
                              volumeProfile: { ...tpoSettings.volumeProfile, pocColor: v },
                            })
                          }
                          onReset={
                            tpoSettings.volumeProfile.pocColor !==
                            DEFAULT_TPO_SETTINGS.volumeProfile.pocColor
                              ? () =>
                                  setTpo({
                                    volumeProfile: {
                                      ...tpoSettings.volumeProfile,
                                      pocColor: DEFAULT_TPO_SETTINGS.volumeProfile.pocColor,
                                    },
                                  })
                              : undefined
                          }
                        />
                      </>
                    )}
                    <TpoLevelRow
                      label="POC"
                      value={tpoSettings.poc}
                      defaultValue={DEFAULT_TPO_SETTINGS.poc}
                      onChange={(poc) => setTpo({ poc })}
                    />
                    <TpoLevelRow
                      label="Value Area"
                      value={tpoSettings.valueArea}
                      defaultValue={DEFAULT_TPO_SETTINGS.valueArea}
                      onChange={(valueArea) => setTpo({ valueArea })}
                    />
                    <TpoLevelRow
                      label="Initial Balance"
                      value={tpoSettings.initialBalance}
                      defaultValue={DEFAULT_TPO_SETTINGS.initialBalance}
                      onChange={(initialBalance) => setTpo({ initialBalance })}
                    />
                    <ToggleRow
                      label="Level labels"
                      value={tpoSettings.levelLabels}
                      onChange={(v) => setTpo({ levelLabels: v })}
                    />
                    <TpoLevelRow
                      label="Price line"
                      value={tpoSettings.line}
                      defaultValue={DEFAULT_TPO_SETTINGS.line}
                      onChange={(line) => setTpo({ line })}
                    />
                    <NumberRow
                      label="Price line opacity %"
                      value={tpoSettings.lineOpacity}
                      min={0}
                      step={5}
                      onChange={(v) => setTpo({ lineOpacity: v })}
                    />
                  </>
                )}
                {styleGroup === 'bars' && (
                  <ColorPairRow
                    label="Bars"
                    upValue={shown('candleUpColor')}
                    downValue={shown('candleDownColor')}
                    onUpChange={(v) => set('candleUpColor', v)}
                    onDownChange={(v) => set('candleDownColor', v)}
                    onReset={
                      overrides('candleUpColor', 'candleDownColor')
                        ? () => resetToTheme('candleUpColor', 'candleDownColor')
                        : undefined
                    }
                  />
                )}
              </Section>
            </>
          )}
          {pane === 'Canvas' && (
            <>
              <Section title="Canvas">
                {/* Outside the themed-color system: default is the CSS canvas
                    var, so the reset is a manual compare against DEFAULT. */}
                <ThemedColorRow
                  label="Background"
                  value={settings.backgroundColor}
                  onChange={(v) => set('backgroundColor', v)}
                  onReset={
                    settings.backgroundColor !== DEFAULT_SETTINGS.backgroundColor
                      ? () => set('backgroundColor', DEFAULT_SETTINGS.backgroundColor)
                      : undefined
                  }
                />
                <ThemedColorRow
                  label="Text color"
                  // Fall back to the real painted default (the same canvas-aware
                  // value useChartStyles paints) so a following swatch highlights,
                  // not the stale #B2B5BE that matched no palette color.
                  value={settings.textColor || defaultAxisTextColor(settings.backgroundColor)}
                  // Picking the current default gray normalizes to '' (follow-
                  // default), so it reads as default (no reset arrow) instead of
                  // pinning an identical color, same fix as the themed candle keys.
                  onChange={(v) =>
                    set(
                      'textColor',
                      sameColor(v, defaultAxisTextColor(settings.backgroundColor)) ? '' : v,
                    )
                  }
                  onReset={settings.textColor ? () => set('textColor', '') : undefined}
                />
                <ThemedColorRow
                  label="Pane separator"
                  value={shown('separatorColor')}
                  onChange={(v) => set('separatorColor', v)}
                  onReset={
                    overrides('separatorColor') ? () => resetToTheme('separatorColor') : undefined
                  }
                />
                <EyeRow
                  label="Chart watermark"
                  hidden={settings.hideWatermark}
                  onChange={(v) => set('hideWatermark', v)}
                />
              </Section>
              <Section title="Margins">
                <NumberRow
                  label="Right margin (bars)"
                  value={settings.rightMarginBars ?? 5}
                  min={0}
                  step={1}
                  onChange={(v) => set('rightMarginBars', v)}
                />
                <NumberRow
                  label="Top margin %"
                  value={settings.topMarginPct ?? 10}
                  min={0}
                  step={1}
                  onChange={(v) => set('topMarginPct', v)}
                />
                <NumberRow
                  label="Bottom margin %"
                  value={settings.bottomMarginPct ?? 10}
                  min={0}
                  step={1}
                  onChange={(v) => set('bottomMarginPct', v)}
                />
              </Section>
              <Section title="Grid">
                {/* Segmented line picker + eye. The eye IS showGrid, the
                    segments ARE gridMode — hiding remembers the line choice,
                    which the old dropdown's "None" entry could not. */}
                <div className="flex min-h-6 items-center justify-between">
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">Grid</span>
                  <div className="flex items-center gap-1.5">
                    <div className="flex items-center gap-0.5 rounded border border-border-strong p-0.5">
                      {GRID_LINE_OPTIONS.map((o) => (
                        <IconButton
                          key={o.value}
                          active={
                            (effectiveGridMode(settings) === 'none'
                              ? (settings.gridMode ?? 'both')
                              : effectiveGridMode(settings)) === o.value
                          }
                          title={o.title}
                          ariaLabel={`Grid lines: ${o.title}`}
                          onClick={() =>
                            onChange({ ...settings, gridMode: o.value, showGrid: true })
                          }
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width="12"
                            height="12"
                            fill="none"
                            stroke="currentColor"
                          >
                            <path d={o.icon} strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </IconButton>
                      ))}
                    </div>
                    <EyeToggle
                      shown={effectiveGridMode(settings) !== 'none'}
                      label="Grid"
                      onChange={(show) =>
                        onChange({
                          ...settings,
                          showGrid: show,
                          gridMode: show
                            ? settings.gridMode && settings.gridMode !== 'none'
                              ? settings.gridMode
                              : 'both'
                            : settings.gridMode,
                        })
                      }
                    />
                  </div>
                </div>
                <SelectRow
                  label="Line style"
                  ariaLabel="Grid line style"
                  value={settings.gridLineStyle ?? 'dashed'}
                  options={[
                    { value: 'solid', label: 'Solid' },
                    { value: 'dashed', label: 'Dashed' },
                    { value: 'dotted', label: 'Dotted' },
                  ]}
                  onChange={(v) => set('gridLineStyle', v as ChartSettings['gridLineStyle'])}
                />
                <ThemedColorRow
                  label="Horizontal color"
                  value={shown('gridHorizontalColor')}
                  onChange={(v) => set('gridHorizontalColor', v)}
                  onReset={
                    overrides('gridHorizontalColor')
                      ? () => resetToTheme('gridHorizontalColor')
                      : undefined
                  }
                />
                <ThemedColorRow
                  label="Vertical color"
                  value={shown('gridVerticalColor')}
                  onChange={(v) => set('gridVerticalColor', v)}
                  onReset={
                    overrides('gridVerticalColor')
                      ? () => resetToTheme('gridVerticalColor')
                      : undefined
                  }
                />
              </Section>
              <Section title="Crosshair">
                <div className="flex min-h-6 items-center justify-between">
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">Crosshair</span>
                  <div className="flex items-center gap-1.5">
                    <div className="flex items-center gap-0.5 rounded border border-border-strong p-0.5">
                      {CROSSHAIR_LINE_OPTIONS.map((o) => (
                        <IconButton
                          key={o.value}
                          active={(settings.crosshairMode ?? 'both') === o.value}
                          title={o.title}
                          ariaLabel={`Crosshair lines: ${o.title}`}
                          onClick={() =>
                            onChange({ ...settings, crosshairMode: o.value, showCrosshair: true })
                          }
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width="12"
                            height="12"
                            fill="none"
                            stroke="currentColor"
                          >
                            <path d={o.icon} strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </IconButton>
                      ))}
                    </div>
                    <EyeToggle
                      shown={settings.showCrosshair !== false}
                      label="Crosshair"
                      onChange={(v) => set('showCrosshair', v)}
                    />
                  </div>
                </div>
                <SelectRow
                  label="Line style"
                  ariaLabel="Crosshair line style"
                  value={settings.crosshairStyle}
                  options={[
                    { value: 'dashed', label: 'Dashed' },
                    { value: 'solid', label: 'Solid' },
                  ]}
                  onChange={(v) => set('crosshairStyle', v as 'dashed' | 'solid')}
                />
                <ThemedColorRow
                  label="Line color"
                  value={shown('crosshairColor')}
                  onChange={(v) => set('crosshairColor', v)}
                  onReset={
                    overrides('crosshairColor') ? () => resetToTheme('crosshairColor') : undefined
                  }
                />
                <ThemedColorRow
                  label="Label background"
                  value={shown('crosshairLabelBg')}
                  onChange={(v) => set('crosshairLabelBg', v)}
                  onReset={
                    overrides('crosshairLabelBg')
                      ? () => resetToTheme('crosshairLabelBg')
                      : undefined
                  }
                />
              </Section>
            </>
          )}
          {pane === 'Scales & zoom' && (
            <>
              <Section title="Price Scale">
                <EyeRow
                  label="Price & time scales"
                  hidden={settings.hideAxisScale}
                  onChange={(v) => set('hideAxisScale', v)}
                />
                <SelectRow
                  label="Price scale fits"
                  value={settings.priceAxisScope}
                  options={[
                    { value: 'indicators', label: 'Candles + indicators' },
                    { value: 'candles', label: 'Candles only' },
                  ]}
                  onChange={(v) => set('priceAxisScope', v as 'candles' | 'indicators')}
                />
                <SelectRow
                  label="Scale type"
                  value={settings.priceScaleType}
                  options={[
                    { value: 'normal', label: 'Regular' },
                    { value: 'percentage', label: 'Percent' },
                    { value: 'log', label: 'Logarithmic' },
                  ]}
                  onChange={(v) => set('priceScaleType', v as 'normal' | 'percentage' | 'log')}
                />
                <ToggleRow
                  label="Invert scale"
                  value={settings.priceScaleInvert}
                  onChange={(v) => set('priceScaleInvert', v)}
                />
                <SelectRow
                  label="Scale position"
                  value={settings.priceScalePosition}
                  options={[
                    { value: 'right', label: 'Right' },
                    { value: 'left', label: 'Left' },
                  ]}
                  onChange={(v) => set('priceScalePosition', v as 'left' | 'right')}
                />
                <ToggleRow
                  label="Fixed price grid"
                  value={settings.fixedPriceGrid}
                  onChange={(v) => set('fixedPriceGrid', v)}
                />
                {settings.fixedPriceGrid && (
                  <NumberRow
                    label="Price increment"
                    value={settings.fixedPriceIncrement}
                    onChange={(v) => set('fixedPriceIncrement', v)}
                    min={0.01}
                    step={0.25}
                    presets={[0.25, 0.5, 1.0, 5.0]}
                  />
                )}
              </Section>
              <Section title="Interaction" icon={<GlobeIcon />} subtitle="Applies to all charts">
                <InteractionPresetRow
                  interactionSettings={interactionSettings}
                  onChange={onInteractionSettingsChange}
                />
                <SelectRow
                  label="Mouse wheel"
                  value={interactionSettings.wheelAction}
                  options={[
                    { value: 'zoom', label: 'Zoom' },
                    { value: 'pan', label: 'Scroll' },
                  ]}
                  onChange={(v) =>
                    onInteractionSettingsChange({ wheelAction: v as 'zoom' | 'pan' })
                  }
                />
                <SelectRow
                  label="Zoom anchor"
                  value={interactionSettings.zoomAnchor}
                  options={[
                    { value: 'cursor', label: 'Cursor' },
                    { value: 'lastBar', label: 'Last bar' },
                  ]}
                  onChange={(v) =>
                    onInteractionSettingsChange({ zoomAnchor: v as 'cursor' | 'lastBar' })
                  }
                />
                <ToggleRow
                  label="Smooth scroll and zoom"
                  value={interactionSettings.smoothMotion ?? true}
                  onChange={(v) => onInteractionSettingsChange({ smoothMotion: v })}
                />
                <ToggleRow
                  label="Reset zoom on ticker change"
                  value={interactionSettings.resetZoomOnSymbolChange ?? false}
                  onChange={(v) => onInteractionSettingsChange({ resetZoomOnSymbolChange: v })}
                />
              </Section>
              <Section title="Reset zoom" icon={<GlobeIcon />} subtitle="Applies to all charts">
                {/* Same size, color and indent as the Section subtitle above it, so the
                    two read as one caption (the children wrapper already carries px-1). */}
                <p className="-mt-1 pb-0.5 text-[10px] text-[hsl(var(--chart-text-muted))]">
                  Double-click a chart or use right-click Reset zoom. Switching timeframes keeps
                  your zoom.
                </p>
                {(
                  [
                    ['intraday', 'Intraday'],
                    ['hourly', 'Hourly'],
                    ['daily', 'Daily'],
                    ['weeklyMonthly', 'Weekly & monthly'],
                  ] as const
                ).map(([category, label]) => (
                  <ResetZoomRow
                    key={category}
                    category={category}
                    label={label}
                    rule={interactionSettings.resetZoom?.[category]}
                    onChange={(rule) =>
                      onInteractionSettingsChange({
                        resetZoom: { ...interactionSettings.resetZoom, [category]: rule },
                      })
                    }
                  />
                ))}
              </Section>
            </>
          )}
          {pane === 'Session & data' && (
            <>
              <Section title="Timezone">
                <SelectRow
                  label="Timezone"
                  value={settings.timezone}
                  options={TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
                  onChange={(v) => set('timezone', v)}
                />
              </Section>
              <Section title="Trading Hours">
                <ToggleRow
                  label="Extended Hours"
                  value={settings.extendedHours}
                  onChange={(v) => set('extendedHours', v)}
                />
                {settings.extendedHours && (
                  <>
                    <ToggleRow
                      label="Extended Hours Highlight"
                      value={settings.extendedHoursHighlight}
                      onChange={(v) => set('extendedHoursHighlight', v)}
                    />
                    {settings.extendedHoursHighlight && (
                      <>
                        <ThemedColorRow
                          label="Pre-market color"
                          value={shown('preMarketColor')}
                          onChange={(v) => set('preMarketColor', v)}
                          onReset={
                            overrides('preMarketColor')
                              ? () => resetToTheme('preMarketColor')
                              : undefined
                          }
                          showOpacity
                        />
                        <ThemedColorRow
                          label="After-hours color"
                          value={shown('afterHoursColor')}
                          onChange={(v) => set('afterHoursColor', v)}
                          onReset={
                            overrides('afterHoursColor')
                              ? () => resetToTheme('afterHoursColor')
                              : undefined
                          }
                          showOpacity
                        />
                      </>
                    )}
                  </>
                )}
                <ToggleRow
                  label="Adjusted prices"
                  value={settings.adjusted}
                  onChange={(v) => set('adjusted', v)}
                />
              </Section>
              <Section title="Reference Lines">
                <ToggleRow
                  label="Previous close"
                  value={settings.showPreviousClose}
                  onChange={(v) => set('showPreviousClose', v)}
                />
                {settings.showPreviousClose && (
                  <ThemedColorRow
                    label="Previous close color"
                    value={settings.previousCloseColor}
                    onChange={(v) => set('previousCloseColor', v)}
                  />
                )}
                <ToggleRow
                  label="Previous day high"
                  value={settings.showPrevDayHigh}
                  onChange={(v) => set('showPrevDayHigh', v)}
                />
                {settings.showPrevDayHigh && (
                  <ThemedColorRow
                    label="Previous day high color"
                    value={settings.prevDayHighColor}
                    onChange={(v) => set('prevDayHighColor', v)}
                  />
                )}
                <ToggleRow
                  label="Previous day low"
                  value={settings.showPrevDayLow}
                  onChange={(v) => set('showPrevDayLow', v)}
                />
                {settings.showPrevDayLow && (
                  <ThemedColorRow
                    label="Previous day low color"
                    value={settings.prevDayLowColor}
                    onChange={(v) => set('prevDayLowColor', v)}
                  />
                )}
                <ToggleRow
                  label="Open"
                  value={settings.showOpen}
                  onChange={(v) => set('showOpen', v)}
                />
                {settings.showOpen && (
                  <ThemedColorRow
                    label="Open color"
                    value={settings.openColor}
                    onChange={(v) => set('openColor', v)}
                  />
                )}
              </Section>
              <Section title="Session Breaks">
                <ToggleRow
                  label="Show session breaks"
                  value={settings.showSessionBreaks}
                  onChange={(v) => set('showSessionBreaks', v)}
                />
                {settings.showSessionBreaks && (
                  <ThemedColorRow
                    label="Break line color"
                    value={shown('sessionBreakColor')}
                    onChange={(v) => set('sessionBreakColor', v)}
                    onReset={
                      overrides('sessionBreakColor')
                        ? () => resetToTheme('sessionBreakColor')
                        : undefined
                    }
                  />
                )}
              </Section>
            </>
          )}
          {pane === 'Markers & labels' && (
            <>
              <Section title="Markers">
                <ToggleRow
                  label="Show earnings"
                  value={settings.showEarnings}
                  onChange={(v) => set('showEarnings', v)}
                />
                <ToggleRow
                  label="Show splits"
                  value={settings.showSplits}
                  onChange={(v) => set('showSplits', v)}
                />
              </Section>
              {/* The two dilution families (warrant strikes, offering prices) are separate
                  toggles over the same overhead-supply story, so they group under their own
                  heading instead of sitting loose among the event markers. Persisted keys
                  are unchanged (showDilutionLevels / showOfferingLevels). */}
              <Section title="Dilution">
                <ToggleRow
                  label="Warrant strikes"
                  value={settings.showDilutionLevels}
                  onChange={(v) => set('showDilutionLevels', v)}
                />
                <ToggleRow
                  label="Offering prices"
                  value={settings.showOfferingLevels}
                  onChange={(v) => set('showOfferingLevels', v)}
                />
              </Section>
              {/* The user's price alerts as lines (utils/priceAlertLine.ts): a data line
                  like the two dilution families above, so it is a switch like them. */}
              {priceAlertsAvailable && (
                <Section title="Alerts">
                  <ToggleRow
                    label="Alert lines"
                    value={settings.showPriceAlertLines}
                    onChange={(v) => set('showPriceAlertLines', v)}
                  />
                </Section>
              )}
              <Section title="Labels">
                <EyeRow
                  label="Overlay OHLC"
                  hidden={settings.hideOverlayOHLC}
                  onChange={(v) => set('hideOverlayOHLC', v)}
                />
                <EyeRow
                  label="Indicator labels"
                  hidden={settings.hideIndicatorLabels}
                  onChange={(v) => set('hideIndicatorLabels', v)}
                />
                <EyeRow
                  label="High/low labels"
                  hidden={settings.hideHighLow}
                  onChange={(v) => set('hideHighLow', v)}
                />
                <EyeRow
                  label="Last price label"
                  hidden={settings.hideLastPriceLabel}
                  onChange={(v) => set('hideLastPriceLabel', v)}
                />
                <EyeRow
                  label="Last price line"
                  hidden={settings.hideLastPriceLine}
                  onChange={(v) => set('hideLastPriceLine', v)}
                />
                <EyeRow
                  label="Pre/post-market price"
                  hidden={settings.hideExtendedSessionPrice}
                  onChange={(v) => set('hideExtendedSessionPrice', v)}
                />
                <EyeRow
                  label="Bar countdown"
                  hidden={settings.hideBarCountdown}
                  onChange={(v) => set('hideBarCountdown', v)}
                />
              </Section>
              {/* Opt-in auto-analysis. Every row here is off by default and tagged
                  Beta; the sub-rows only appear once their feature is on so the
                  section stays four lines tall for anyone not trying them. */}
              <Section title="Beta features" subtitle="Early features. May change or be removed.">
                {(settings.patternDetectionEnabled ||
                  settings.autoFibEnabled ||
                  settings.autoTrendlinesEnabled) && (
                  // One dial for every detector below: whether a swing or a
                  // touch is measured on the candle's wick or its body. Shown
                  // once any feature is on, so the section stays three lines
                  // for anyone not trying them.
                  <SelectRow
                    label="Touches use"
                    value={settings.autoAnalysisTouchInput}
                    options={[
                      { value: 'wicks', label: 'Wicks' },
                      { value: 'bodies', label: 'Candle bodies' },
                    ]}
                    onChange={(v) => set('autoAnalysisTouchInput', v as TouchInput)}
                  />
                )}
                <ToggleRow
                  label="Support / resistance levels"
                  badge={<BetaTag />}
                  value={settings.patternDetectionEnabled}
                  onChange={(v) => set('patternDetectionEnabled', v)}
                />
                {settings.patternDetectionEnabled && (
                  <NumberRow
                    label="Min confidence %"
                    value={Math.round(settings.patternDetectionMinConfidence * 100)}
                    onChange={(v) =>
                      set('patternDetectionMinConfidence', Math.max(0, Math.min(1, v / 100)))
                    }
                    min={0}
                    step={5}
                    presets={[0, 20, 40, 60, 80]}
                  />
                )}
                <ToggleRow
                  label="Auto Fibonacci"
                  badge={<BetaTag />}
                  value={settings.autoFibEnabled}
                  onChange={(v) => set('autoFibEnabled', v)}
                />
                {settings.autoFibEnabled && (
                  <NumberRow
                    label="Swing size (ATR)"
                    value={settings.autoFibSwingMult}
                    onChange={(v) => set('autoFibSwingMult', Math.max(1, v))}
                    min={1}
                    step={1}
                    presets={[2, 3, 5]}
                  />
                )}
                <ToggleRow
                  label="Auto trendlines"
                  badge={<BetaTag />}
                  value={settings.autoTrendlinesEnabled}
                  onChange={(v) => set('autoTrendlinesEnabled', v)}
                />
                {settings.autoTrendlinesEnabled && (
                  <NumberRow
                    label="Lines per side"
                    value={settings.autoTrendlinesMaxPerSide}
                    onChange={(v) => set('autoTrendlinesMaxPerSide', Math.max(1, Math.round(v)))}
                    min={1}
                    step={1}
                    presets={[1, 2, 3, 5]}
                  />
                )}
              </Section>
              {extraSections}
            </>
          )}
        </div>
      </div>
      {/* Footer (sticky): Templates popover + Reset to Default */}
      <div className="relative flex shrink-0 items-center justify-between border-t border-border-strong px-3 py-2.5">
        {profilesEnabled ? (
          <div className="relative">
            <button
              type="button"
              data-testid="templates-button"
              onClick={() => setTemplatesOpen((o) => !o)}
              aria-expanded={templatesOpen}
              className={
                'flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-medium transition-colors ' +
                (templatesOpen
                  ? 'bg-accent text-[hsl(var(--chart-text))]'
                  : 'text-[hsl(var(--muted-foreground))] hover:bg-accent hover:text-[hsl(var(--chart-text))]')
              }
            >
              <BookmarkIcon />
              Templates
              <span
                className="inline-flex transition-transform"
                style={{ transform: templatesOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              >
                <Icon size={11}>
                  <polyline points="6 9 12 15 18 9" />
                </Icon>
              </span>
            </button>

            {templatesOpen && (
              <div
                data-testid="settings-profiles"
                className="absolute bottom-full left-0 z-10 mb-2 flex w-[300px] flex-col gap-2 rounded-lg border border-border-strong bg-popover p-2.5 shadow-xl shadow-black/40"
              >
                <div className="flex gap-1.5">
                  <input
                    data-testid="profile-name-input"
                    type="text"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveProfile()
                    }}
                    placeholder="Save current as…"
                    className="h-6 min-w-0 flex-1 rounded border border-border-strong bg-field px-2 text-[11px] text-[hsl(var(--chart-text))] outline-hidden placeholder:text-[hsl(var(--muted-foreground))] focus:border-primary"
                  />
                  <button
                    type="button"
                    data-testid="save-profile-button"
                    onClick={handleSaveProfile}
                    disabled={profileName.trim() === ''}
                    className={
                      'h-6 rounded px-2.5 text-[11px] font-medium transition-colors ' +
                      (profileName.trim()
                        ? 'bg-primary text-primary-foreground hover:opacity-90'
                        : 'cursor-not-allowed border border-border-strong bg-background text-[hsl(var(--muted-foreground))]')
                    }
                  >
                    Save
                  </button>
                </div>
                {settingsProfiles!.length > 0 ? (
                  <div
                    className="flex max-h-[220px] flex-col gap-1 overflow-y-auto"
                    style={{ scrollbarWidth: 'thin' }}
                  >
                    {settingsProfiles!.map((profile) => (
                      <div key={profile.id} className="flex items-center gap-1">
                        <button
                          type="button"
                          data-testid={`load-profile-${profile.id}`}
                          onClick={() => onLoadSettingsProfile?.(profile.id)}
                          className="h-6 min-w-0 flex-1 truncate rounded px-2 text-left text-[11px] text-[hsl(var(--chart-text))] transition-colors hover:bg-accent"
                        >
                          {profile.name}
                        </button>
                        {onDeleteSettingsProfile && (
                          <button
                            type="button"
                            data-testid={`delete-profile-${profile.id}`}
                            onClick={() => onDeleteSettingsProfile(profile.id)}
                            aria-label={`Delete ${profile.name}`}
                            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-[hsl(var(--muted-foreground))] transition-colors hover:bg-accent hover:text-[hsl(var(--chart-text))]"
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-1 py-0.5 text-[11px] text-[hsl(var(--muted-foreground))]">
                    No saved templates yet.
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="reset-defaults-button"
            onClick={() => onChange({ ...DEFAULT_SETTINGS })}
            className="rounded px-2 py-1 text-[11px] font-medium text-[hsl(var(--muted-foreground))] transition-colors hover:bg-accent hover:text-[hsl(var(--chart-text))]"
          >
            Reset to Default
          </button>
          <button
            type="button"
            data-testid="settings-done-button"
            onClick={onClose}
            className={primaryButtonCls(false)}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(panel, document.body)
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Kolkata',
  'Australia/Sydney',
  'UTC',
]
